import { useEffect, useState } from "react";
import { webhookApi } from "@/lib/api-client";
import { AlertOctagon, Radio, ExternalLink, CheckCircle2, RefreshCw } from "lucide-react";
import { relativeTime } from "@/lib/format";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PRIORITY_COLOR = {
  critical: "#EF4444",
  high: "#F59E0B",
  medium: "#3B82F6",
  low: "#71717A",
  none: "#525252",
};

export default function FixPriorityQueue({ refreshKey }) {
  const [queue, setQueue] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [q, e] = await Promise.all([webhookApi.fixQueue(), webhookApi.listEvents(true)]);
      setQueue(q);
      setEvents(e);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const resolve = async (id) => {
    try {
      await webhookApi.resolve(id);
      toast.success("Event resolved");
      load();
    } catch { toast.error("Failed to resolve"); }
  };

  const eventsByVenture = events.reduce((acc, e) => {
    (acc[e.venture_id] ||= []).push(e);
    return acc;
  }, {});

  return (
    <section className="uc-card p-6 uc-fade" data-testid="fix-priority-queue">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="uc-chip mb-2">
            <span className="uc-dot bg-red-500 uc-pulsing"></span>
            FIX PRIORITY · REAL-TIME
          </div>
          <h2 className="font-serif-display text-2xl">Live Ops Board</h2>
          <p className="text-white/50 text-xs mt-1">Webhook-driven incidents. Sentinel assesses, Executor drafts resolution.</p>
        </div>
        <button data-testid="fix-refresh-btn" onClick={load} className="uc-chip hover:bg-white/5 transition-colors uc-focus">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
      </div>

      {queue.length === 0 ? (
        <div className="p-6 border border-white/5 bg-black/40 text-center">
          <Radio size={22} className="text-emerald-400 mx-auto mb-2"/>
          <div className="font-mono-uc text-[11px] uppercase tracking-widest text-emerald-400 mb-1">All Clear</div>
          <div className="text-white/50 text-xs">No real-time incidents. Wire a webhook to a venture to activate this board.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {queue.map((v) => {
            const color = PRIORITY_COLOR[v.fix_priority] || PRIORITY_COLOR.medium;
            const vEvents = eventsByVenture[v.id] || [];
            return (
              <div key={v.id} className="border border-white/10 hover:border-white/20 transition-colors" style={{ borderLeft: `3px solid ${color}` }} data-testid={`fix-row-${v.id}`}>
                <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <AlertOctagon size={16} style={{ color }}/>
                    <div className="min-w-0">
                      <div className="text-sm text-white font-medium truncate">{v.name}</div>
                      <div className="font-mono-uc text-[10px] text-white/50 uppercase tracking-wider">
                        {v.category?.replace("_", " ")} · Last event {relativeTime(v.last_webhook_event_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-uc text-[10px] uppercase tracking-widest px-2 py-0.5 border" style={{ borderColor: color, color }}>
                      {v.fix_priority}
                    </span>
                    <span className="font-mono-uc text-[10px] text-white/40">
                      {v.unresolved_events} open
                    </span>
                  </div>
                </div>
                {vEvents.length > 0 && (
                  <div className="border-t border-white/5 bg-black/40">
                    {vEvents.slice(0, 4).map((e) => (
                      <div key={e.id} className="p-2.5 pl-8 border-b border-white/5 last:border-b-0 flex items-start justify-between gap-2" data-testid={`event-${e.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] text-white/85 flex items-center gap-2 flex-wrap">
                            <span className="font-mono-uc text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: PRIORITY_COLOR[e.severity], color: PRIORITY_COLOR[e.severity] }}>
                              {e.severity}
                            </span>
                            <span>{e.event_type}</span>
                          </div>
                          {e.message && <div className="text-[11px] text-white/50 mt-0.5">{e.message}</div>}
                          <div className="font-mono-uc text-[10px] text-white/40 mt-1">{relativeTime(e.created_at)}</div>
                        </div>
                        <button
                          data-testid={`resolve-event-${e.id}`}
                          onClick={() => resolve(e.id)}
                          className="text-emerald-400 hover:text-emerald-300 text-[10px] font-mono-uc uppercase tracking-wider flex items-center gap-1 border border-emerald-500/30 px-2 py-1 hover:bg-emerald-500/10 transition-colors"
                        >
                          <CheckCircle2 size={10}/> Resolve
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
